import Vue from "vue";
import Vuex from "vuex";
import api from "@/api";
import * as CONSTANTS from "@/store/constants";
import router from "@/router";
import parseKnowledge from "@/store/parseKnowledge";

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    user: {},
    users: {},
    announcements: [],
    authResponse: "",

    ticketsLoading: false,
    totalTickets: undefined,
    userPasswordExpiryDays: undefined,
    isLoading: true,
    loadingServiceDirectories: true,
    articlesLoading: true,
    standardLoading: true,
    searchReady: false,
    userServicesByRole: [],
    isFilteredByRole: false,
    itServices: new Map(),
    businessServices: new Map(),
    serviceTaxonomy: [],
    serviceDirectoryTaxonomny: [],
    allServiceDirectoryTaxonomny: [],
    relatedServiceDirectoryTaxonomny: [],
    hrTaxonomy: [],
    country: null,
    standardCategories: [],
    standardArticles: [],
    standardServices: [],
    articleContent: {},
    articleComments: [],
    platformCategories: [],
    serviceDirectoryCategories: [],
    relatedServiceAreaDirectories: Object.create(null),
    errorMessage: "",
    successMessage: "",
    quickSolutions: [],
    articleContentLoading: false,
    platformCategory: "",
    currentCategory: undefined,
    currentServiceCategory: undefined,
    currentTaxonomyCategory: undefined,
    currentTaxonomySubCategory: undefined,
    inputFilter: "",
    filteredArticles: [],
    contentPageModel: [],
    platformCategoryName: "",
    contentPageNavigationText: "",
    service: "",
    userCriteria: null,
    getStartedClicked: false,
  },
  getters: {
    successMessage(state) {
      return state.successMessage;
    },
    user(state) {
      if (!state.authResponse) return undefined;
      return state.authResponse.user;
    },
    getCommentUser(state) {
      return function (userid) {
        if (state.users[userid] === undefined) {
          // Set with temporary data while loading
          var userData = {
            name: userid,
            image: "none",
            nameLoadState: CONSTANTS.LOAD_STATE_NONE,
            imageLoadState: CONSTANTS.LOAD_STATE_NONE
          };
          Vue.set(state.users, userid, userData);
        }
        return state.users[userid];
      };
    },
    standardContentAccessible(state) {
      if (
        !state.authResponse ||
        !state.authResponse.user ||
        !state.authResponse.user.authorities
      )
        return false;
      if (
        state.authResponse.user.authorities.includes(
          CONSTANTS.EMPLOYEE_HUB_HR_SECURE_READ_ROLE
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    // Returns the user name for a user, from the user management endpoint
    userName(state, getters) {
      return function (userid) {
        var user = getters.getCommentUser(userid);
        if (user.nameLoadState == CONSTANTS.LOAD_STATE_NONE) {
          user.nameLoadState = CONSTANTS.LOAD_STATE_LOADING;
          api
            .getUserInfo(userid)
            .then(data => {
              user.name = data.data.fullName;
            })
            .catch(err => {
              console.warn(CONSTANTS.WARN_NO_USER_DATA + userid);
            })
            .finally(() => {
              user.nameLoadState = CONSTANTS.LOAD_STATE_LOADED;
            });
        }
        return user.name;
      };
    },
    userNameLoadState(state, getters) {
      return function (userid) {
        var user = getters.getCommentUser(userid);
        getters.userName(userid);
        return user.nameLoadState;
      };
    },
    // Returns the user image for a user, from the profile picture service
    // Also sets the full display name if available
    userImage(state, getters) {
      return function (userid) {
        var user = getters.getCommentUser(userid);
        if (user.imageLoadState == CONSTANTS.LOAD_STATE_NONE) {
          user.imageLoadState = CONSTANTS.LOAD_STATE_LOADING;
          api
            .getProfilePicture(userid)
            .then(data => {
              if (data.displayName) {
                user.name = data.data.displayName;
                user.nameLoadState = CONSTANTS.LOAD_STATE_LOADED;
              }
              user.image = data.data.base64EncodedImage;
            })
            .catch(err => {
              console.warn(CONSTANTS.WARN_NO_PROFILE_PIC + userid);
              user.image = "none";
            })
            .finally(() => {
              user.imageLoadState = CONSTANTS.LOAD_STATE_LOADED;
            });
        }
        return user.image;
      };
    },
    userImageLoadState(state, getters) {
      return function (userid) {
        var user = getters.getCommentUser(userid);
        getters.userImage(userid);
        return user.imageLoadState;
      };
    },
    authResponse(state) {
      return state.authResponse;
    },
    country(state) {
      return state.country;
    },
    announcements(state) {
      return state.announcements;
    },
    totalTickets(state) {
      return state.totalTickets;
    },
    ticketsLoading(state) {
      return state.ticketsLoading;
    },
    userPasswordExpiryDays(state) {
      return state.userPasswordExpiryDays;
    },
    isLoading(state) {
      return state.isLoading;
    },
    loadingServiceDirectories(state) {
      return state.loadingServiceDirectories;
    },
    standardLoading(state) {
      return state.standardLoading;
    },
    currentCategory(state) {
      return state.currentCategory;
    },
    currentServiceCategory(state) {
      return state.currentServiceCategory;
    },
    currentTaxonomyCategory(state) {
      return state.currentTaxonomyCategory;
    },
    currentTaxonomySubCategory(state) {
      return state.currentTaxonomySubCategory;
    },
    platformCategory(state) {
      return state.platformCategory;
    },
    articlesLoading(state) {
      return state.articlesLoading;
    },
    userServicesByRole(state) {
      return state.userServicesByRole;
    },
    isFilteredByRole(state) {
      return state.isFilteredByRole;
    },
    serviceTaxonomy(state) {
      return state.serviceTaxonomy;
    },
    allServiceDirectoryTaxonomny(state) {
      return state.allServiceDirectoryTaxonomny;
    },
    serviceDirectoryTaxonomny(state) {
      return state.serviceDirectoryTaxonomny;
    },
    hrTaxonomy(state) {
      return state.hrTaxonomy;
    },
    relatedServiceDirectoryTaxonomny(state) {
      return state.relatedServiceDirectoryTaxonomny;
    },
    standardCategories(state) {
      return state.standardCategories;
    },
    platformCategories(state) {
      return state.platformCategories;
    },
    serviceDirectoryCategories(state) {
      return state.serviceDirectoryCategories;
    },
    relatedServiceAreaDirectories(state) {
      return state.relatedServiceAreaDirectories;
    },
    errorMessage(state) {
      return state.errorMessage;
    },
    quickSolutions(state) {
      return state.quickSolutions;
    },
    searchReady(state) {
      return state.searchReady;
    },
    standardArticles(state) {
      return state.standardArticles;
    },
    standardServices(state) {
      return state.standardServices;
    },
    articleContent(state) {
      return state.articleContent;
    },
    articleContentLoading(state) {
      return state.articleContentLoading;
    },
    articleComments(state) {
      return state.articleComments;
    },
    inputFilter(state) {
      return state.inputFilter;
    },
    filteredArticles(state) {
      return state.filteredArticles;
    },
    contentPageModel(state) {
      return state.contentPageModel;
    },
    platformCategoryName(state) {
      return state.platformCategoryName;
    },
    contentPageNavigationText(state) {
      return state.contentPageNavigationText;
    },
    service(state) {
      return state.service;
    },
    userCriteria(state) {
      return state.userCriteria
    },
    getStartedClicked(state) {
      return state.getStartedClicked
    }
  },
  mutations: {
    onStartLoading(state) {
      state.numLoading += 1;
    },
    onFinishedLoading(state) {
      state.numLoading -= 1;
    },
    setSuccessMessage(state, successMessage) {
      state.successMessage = successMessage;
    },
    setServiceTaxonomy(state, serviceTaxonomy) {
      state.serviceTaxonomy = serviceTaxonomy;
    },
    setAllServiceDirectoryTaxonomny(state, allServiceDirectoryTaxonomny) {
      state.allServiceDirectoryTaxonomny = allServiceDirectoryTaxonomny;
    },
    setServiceDirectoryTaxonomny(state, serviceDirectoryTaxonomny) {
      state.serviceDirectoryTaxonomny = serviceDirectoryTaxonomny;
    },
    //relatedServiceDirectoryTaxonomny
    setRelatedServiceDirectoryTaxonomny(
      state,
      relatedServiceDirectoryTaxonomny
    ) {
      state.relatedServiceDirectoryTaxonomny = relatedServiceDirectoryTaxonomny;
    },
    setHrTaxonomy(state, hrTaxonomy) {
      state.hrTaxonomy = hrTaxonomy;
    },
    setStandardCategories(state, standardCategories) {
      state.standardCategories = standardCategories;
    },
    setCountry(state, country) {
      state.country = country;
    },
    setAnnouncements(state, announcements) {
      state.announcements = announcements;
    },
    setQuickSolutions(state, quickSolutions) {
      state.quickSolutions = quickSolutions;
    },
    setTotalTickets(state, totalTickets) {
      state.totalTickets = totalTickets;
    },
    setAuthResponse(state, authResponse) {
      state.authResponse = authResponse;
    },
    setTicketsLoading(state, isLoading) {
      state.ticketsLoading = isLoading;
    },
    setPlatformCategory(state, platformCategory) {
      state.platformCategory = platformCategory;
    },
    setUserPasswordExpiryDays(state, userPasswordExpiryDays) {
      state.userPasswordExpiryDays = userPasswordExpiryDays;
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setLoadingServiceDirectories(state, loadingServiceDirectories) {
      state.loadingServiceDirectories = loadingServiceDirectories;
    },

    setStandardLoading(state, standardLoading) {
      state.standardLoading = standardLoading;
    },
    setCurrentCategory(state, currentCategory) {
      state.currentCategory = currentCategory;
    },
    setCurrentServiceCategory(state, currentServiceCategory) {
      state.currentServiceCategory = currentServiceCategory;
    },
    setCurrentTaxonomyCategory(state, currentTaxonomyCategory) {
      state.currentTaxonomyCategory = currentTaxonomyCategory;
    },
    setCurrentTaxonomySubCategory(state, currentTaxonomySubCategory) {
      state.currentTaxonomySubCategory = currentTaxonomySubCategory;
    },
    setArticlesLoading(state, articlesLoading) {
      state.articlesLoading = articlesLoading;
    },
    setUserServicesByRole(state, userServicesByRole) {
      state.userServicesByRole = userServicesByRole;
    },
    setIsFilteredByRole(state, isFilteredByRole) {
      state.isFilteredByRole = isFilteredByRole;
    },
    setPlatformCategories(state, platformCategories) {
      state.platformCategories = platformCategories;
    },
    setServiceDirectoryCategories(state, serviceDirectoryCategories) {
      state.serviceDirectoryCategories = serviceDirectoryCategories;
    },
    setRelatedServiceAreaDirectories(state, relatedServiceDirectoriesObject) {
      Vue.set(
        state.relatedServiceAreaDirectories,
        relatedServiceDirectoriesObject.key,
        relatedServiceDirectoriesObject.value
      );
    },
    setErrorMessage(state, errorMessage) {
      state.errorMessage = errorMessage;
    },
    setSearchReady(state, searchReady) {
      state.searchReady = searchReady;
    },
    setStandardArticles(state, standardArticles) {
      state.standardArticles = standardArticles;
    },
    setStandardServices(state, standardServices) {
      state.standardServices = standardServices;
    },
    setArticleContent(state, articleContent) {
      state.articleContent = articleContent;
    },
    setIsArticleContentLoading(state, articleContentLoading) {
      state.articleContentLoading = articleContentLoading;
    },
    setArticleComments(state, articleComments) {
      state.articleComments = articleComments
    },
    setInputFilter(state, inputFilter) {
      state.inputFilter = inputFilter;
    },
    setFilteredArticles(state, filteredArticles) {
      state.filteredArticles = filteredArticles;
    },
    setContentPageModel(state, contentPageModel) {
      state.contentPageModel = contentPageModel;
    },
    setPlatformCategoryName(state, platformCategoryName) {
      state.platformCategoryName = platformCategoryName;
    },
    setContentPageNavigationText(state, contentPageNavigationText) {
      state.contentPageNavigationText = contentPageNavigationText;
    },
    setService(state, service) {
      state.service = service;
    },
    setUserCriteria(state, userCriteria) {
      state.userCriteria = userCriteria
    },
    setGetStartedClicked(state, getStartedClicked) {
      state.getStartedClicked = getStartedClicked
    }
  },
  actions: {
    setBannerSuccessMessage({ commit, state }, successMessage) {
      commit("setSuccessMessage", successMessage);
      if (this.timeout) {
        clearInterval(this.timeout);
      }
      this.timeout = setTimeout(() => {
        commit("setSuccessMessage", "");
      }, 10000);
    },
    setBannerErrorMessage({ commit, state }, errorMessage) {
      commit("setErrorMessage", errorMessage);
    },
    fetchPlatformCategories({ commit, state }) {
      commit("setIsLoading", true);
      return api
        .getPlatformCategories()
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let platformCategories = response.data.data.getAllAppRecords;
            commit("setPlatformCategories", platformCategories);
          }
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    async fetchRelatedServiceDirectories({ commit, dispatch, state }) {
      commit("setLoadingServiceDirectories", true);
      await dispatch("fetchPlatformCategories");
      let salesRelatedDirectories = [];
      let filteredCategories = state.platformCategories.filter(directory => {
        var isWWFOdirectory = directory.alias.indexOf("WWFO") > -1;
        if (isWWFOdirectory) {
          salesRelatedDirectories.push(directory.alias);
        }
        return !isWWFOdirectory;
      });
      let relatedServiceDirectoriesObject = {
        key: CONSTANTS.SALES_DIRECTORY,
        value: salesRelatedDirectories
      };
      commit("setServiceDirectoryCategories", filteredCategories);
      commit(
        "setRelatedServiceAreaDirectories",
        relatedServiceDirectoriesObject
      );
      commit("setLoadingServiceDirectories", false);
    },
    fetchStandardCategories({ commit, state }, platformCategory) {
      commit("setIsLoading", true);
      return api
        .getStandardCategories(platformCategory)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let standardTaxonomy = response.data.data.standardTaxonomy;
            if (standardTaxonomy) {
              //Sort by name
              standardTaxonomy = standardTaxonomy.sort((c1, c2) =>
                c1.name.localeCompare(c2.name)
              );
            }
            commit("setStandardCategories", standardTaxonomy);
          }
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchArticlesCategories({ commit, state }, { platformCategory }) {
      commit("setIsLoading", true);
      return api
        .getArticleCategories(platformCategory)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let knowledgeCategories = response.data.data.getKnowledgeCategories;
            if (knowledgeCategories) {
              //Sort by name
              knowledgeCategories = knowledgeCategories.sort((c1, c2) =>
                c1.name.localeCompare(c2.name)
              );
            }
            commit("setStandardCategories", knowledgeCategories);
          }
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchStandardArticles({ commit, state }, { platformCategory, serviceId }) {
      commit("setArticlesLoading", true);
      return api
        .getStandardArticles(platformCategory, serviceId)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let standardArticles = response.data.data.getService.articles;
            commit("setStandardArticles", standardArticles);
            commit("setErrorMessage", null);
          }
        })
        .finally(() => {
          commit("setArticlesLoading", false);
        });
    },
    fetchPlatformArticles({ commit, state }, { platformCategory, serviceId }) {
      commit("setArticlesLoading", true);
      return api
        .getPlatformArticles(platformCategory, serviceId)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let standardArticles =
              response.data.data.getKnowledgeCategory.articles;
            commit("setStandardArticles", standardArticles);
            commit("setErrorMessage", null);
          }
        })
        .finally(() => {
          commit("setArticlesLoading", false);
        });
    },
    fetchArticleContent(
      { commit, state },
      { platformCategory, knowledgeBrowseId }
    ) {
      return api
        .getArticleContent(platformCategory, knowledgeBrowseId)
        .then(response => {
          if (
            response.data.errors &&
            response.data.errors[0].message.indexOf("not found") === -1
          ) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let articleContent = response.data.data.knowledgeById;
            if(articleContent){
              articleContent = parseKnowledge.checkForAndProcessArticleFaqs(articleContent);
              commit("setErrorMessage", null);
            }
            commit("setArticleContent", articleContent);
          }
        });
    },
    fetchArticleComments(
      { commit, state },
      { articleId }
    ) {
      return api
        .getArticleComments(articleId)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let articleComments = response.data.result;
            commit("setArticleComments", articleComments);
          }
        });
    },
    fetchWolkenArticleComments(
      { commit, state },
      { articleId }
    ) {
      return api
        .getWolkenArticleComments(articleId)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let articleComments = response.data.result;
            commit("setArticleComments", articleComments);
          }
        });
    },
    fetchHRServiceDirectoryTaxonomy({ commit, state }) {
      commit("setLoadingServiceDirectories", true);
      return api
        .getStandardCategories(CONSTANTS.HR_PLATFORM)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let standardTaxonomy = response.data.data.standardTaxonomy;
            var hrServices = [
              { name: CONSTANTS.HR_SERVICES_AREA, services: standardTaxonomy }
            ];
            var hrTaxonomy = [
              { name: CONSTANTS.HR_DIRECTORY, serviceLines: hrServices }
            ];
            commit("setHrTaxonomy", hrTaxonomy);
            return hrTaxonomy;
          }
        })
        .finally(() => {
          commit("setLoadingServiceDirectories", false);
        });
    },
    async fetchRelatedServiceDirectoriesTaxonomy(
      { commit, dispatch, state },
      relatedDirectoryObject
    ) {
      commit("setLoadingServiceDirectories", true);
      let serviceDirectoryHierarchyTaxonomy = [];
      await api
        .getServiceHierarchyTaxonomy(relatedDirectoryObject.directory)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let relatedHierarchyTaxonomy =
              response.data.data.serviceHierarchyTaxonomy;
            relatedHierarchyTaxonomy.forEach(sa => {
              sa.serviceLines = sa.serviceLines.filter(
                sl => sl.services && sl.services.length > 0
              );
            });
            return relatedHierarchyTaxonomy;
          }
        })
        .then(relatedHierarchyTaxonomy => {
          serviceDirectoryHierarchyTaxonomy = relatedHierarchyTaxonomy;
          state.relatedServiceAreaDirectories[
            relatedDirectoryObject.directory
          ].forEach(relatedDirectory => {
            return api
              .getServiceHierarchyTaxonomy(relatedDirectory)
              .then(response => {
                if (response.data.errors) {
                  commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
                } else {
                  let hierarchyTaxonomy =
                    response.data.data.serviceHierarchyTaxonomy;
                  if (
                    hierarchyTaxonomy != undefined &&
                    hierarchyTaxonomy.length != 0
                  ) {
                    hierarchyTaxonomy.forEach(sa => {
                      sa.serviceLines = sa.serviceLines.filter(
                        sl => sl.services && sl.services.length > 0
                      );
                    });
                    serviceDirectoryHierarchyTaxonomy[0].serviceLines.push.apply(
                      serviceDirectoryHierarchyTaxonomy[0].serviceLines,
                      hierarchyTaxonomy[0].serviceLines
                    );
                    serviceDirectoryHierarchyTaxonomy[0].serviceLines.sort(
                      (a, b) => (a.name > b.name ? 1 : -1)
                    );
                  }
                }
              });
          });
        })
        .finally(() => {
          if (!relatedDirectoryObject.isAllDirectoriesRequest) {
            commit(
              "setServiceDirectoryTaxonomny",
              serviceDirectoryHierarchyTaxonomy
            );
          }
          commit(
            "setRelatedServiceDirectoryTaxonomny",
            serviceDirectoryHierarchyTaxonomy
          );
          commit("setLoadingServiceDirectories", false);
        });
    },
    async fetchAllServiceDirectoriesTaxonomy(
      { commit, state },
      allDirectoriesTaxonomny
    ) {
      await state.serviceDirectoryCategories.forEach(category => {
        var serviceDirectory = category.alias;
        if (
          serviceDirectory != CONSTANTS.HR_PLATFORM &&
          !(serviceDirectory in state.relatedServiceAreaDirectories)
        ) {
          return api
            .getServiceHierarchyTaxonomy(serviceDirectory)
            .then(response => {
              if (response.data.errors) {
                commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
              } else {
                let serviceHierarchyTaxonomy =
                  response.data.data.serviceHierarchyTaxonomy;
                serviceHierarchyTaxonomy.forEach(sa => {
                  sa.serviceLines = sa.serviceLines.filter(
                    sl => sl.services && sl.services.length > 0
                  );
                });
                if (
                  serviceHierarchyTaxonomy != undefined &&
                  serviceHierarchyTaxonomy.length != 0
                ) {
                  allDirectoriesTaxonomny.push.apply(
                    allDirectoriesTaxonomny,
                    serviceHierarchyTaxonomy
                  );
                }
              }
            });
        }
      });
      commit("setServiceDirectoryTaxonomny", allDirectoriesTaxonomny);
      commit("setAllServiceDirectoryTaxonomny", allDirectoriesTaxonomny);
      commit("setLoadingServiceDirectories", false);
    },
    fetchServiceDirectoryTaxonomy({ commit, state }, platformCategory) {
      commit("setLoadingServiceDirectories", true);
      return api
        .getServiceHierarchyTaxonomy(platformCategory)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
            this.$router.push({ name: "page-not-found" });
          } else {
            let serviceHierarchyTaxonomy =
              response.data.data.serviceHierarchyTaxonomy;
            serviceHierarchyTaxonomy.forEach(sa => {
              sa.serviceLines = sa.serviceLines.filter(
                sl => sl.services && sl.services.length > 0
              );
            });
            commit("setServiceDirectoryTaxonomny", serviceHierarchyTaxonomy);
            return serviceHierarchyTaxonomy;
          }
        })
        .finally(() => {
          commit("setLoadingServiceDirectories", false);
        });
    },

    fetchServiceTaxonomy({ commit, state }, platformCategory) {
      commit("setIsLoading", true);

      return api
        .getServiceHierarchyTaxonomy(platformCategory)
        .then(response => {
          if (response.data.errors) {
            response.data.errors.forEach(error => {
              if (error.message.includes('Forbidden')) {
                router.replace({ name: "Forbidden" });
              }
            })
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let serviceHierarchyTaxonomy =
              response.data.data.serviceHierarchyTaxonomy;
            serviceHierarchyTaxonomy.forEach(sa => {
              sa.serviceLines = sa.serviceLines.filter(
                sl => sl.services && sl.services.length > 0
              );
            });

            commit("setServiceTaxonomy", serviceHierarchyTaxonomy);
          }
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchUserServicesByRole({ commit, state }, isByRoleRequest) {
      if (!isByRoleRequest) commit("setIsLoading", true);
      const filteredPreferences = [
        {
          recommendationFor: state.authResponse.user.userName,
          type: "byRole"
        }
      ];
      return api
        .getRecommendationsByService(
          state.authResponse.user.userName,
          filteredPreferences
        )
        .then(response => {
          commit(
            "setUserServicesByRole",
            response.data.data.byRole[0].recommendations
          );
        })
        .finally(() => {
          commit("setIsLoading", false);
        });
    },
    fetchUserPasswordExpiryDays({ commit, state }) {
      return api
        .getUserPasswordExpiryDays(state.authResponse.user.userName)
        .then(response => {
          commit("setUserPasswordExpiryDays", response.data.daysPasswordExpire);
        });
    },
    fetchTickets({ commit, state }) {
      commit("setTicketsLoading", true);
      return api
        .getTickets()
        .then(response => {
          var totalTickets = response.data.totalTickets;
          commit("setTotalTickets", totalTickets);
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => {
          commit("setTicketsLoading", false);
        });
    },
    fetchAnnouncements({ commit, state }) {
      return api
        .getAnnouncementsByService()
        .then(response => {
          var announcements = response.data.data.announcements;
          if (announcements) {
            commit("setAnnouncements", announcements);
          }
        })
        .catch(e => {
          console.error(e);
        });
    },
    fetchQuickSolutions({ commit, state }) {
      return api
        .quickSolutions()
        .then(response => {
          var quickSolutions = response.data.data.quickSolutions;
          if (quickSolutions) {
            commit("setQuickSolutions", quickSolutions);
          }
        })
        .catch(e => {
          console.error(e);
        });
    },
    saveArticleComments(
      { commit, state },
      { platformCategory, articleId, comments }
    ) {
      return api
        .setArticleComments(platformCategory, articleId, comments)
        .then(response => {
          if (response.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          }
        })
        .catch(e => {
          console.error(e);
        });
    },



    saveArticleFeedback(
      { commit, state, getters },
      { platformCategory, requestBody }
    ) {

      let wolkenUsePlatformCategory = getters.platformCategories.find(category => category.alias === platformCategory);
      const useWolken = wolkenUsePlatformCategory && wolkenUsePlatformCategory.useWolken;
      const feedbackApiCall = useWolken
        ? api.setWolkenArticleFeedback(requestBody, platformCategory)
        : api.setArticleFeedback(requestBody);
    
      return feedbackApiCall
        .then(feedbackResponse => {
          if (feedbackResponse.data.errors) {
            commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
          } else {
            let platformCategoryConfig = getters.platformCategories.find(category => category.alias === platformCategory);
            let createFeedbackTask = (platformCategoryConfig &&
              platformCategoryConfig.knowledgeArticleFeedbackExperience &&
              platformCategoryConfig.knowledgeArticleFeedbackExperience === "Enable");
    
            if (!useWolken && createFeedbackTask && feedbackResponse.data.result && feedbackResponse.data.result.sys_id) {
              let kbFeedbackTaskRequestBody = {
                "feedback": feedbackResponse.data.result.sys_id,
                "assigned_to": "",
                "feedback_task_type": "User feedback",
                "u_string_1": getters.articleContent.kbNumber,
                "description": feedbackResponse.data.result.comments,
              };
    
              return api
                .setArticleFeedbackTask(kbFeedbackTaskRequestBody)
                .then((response) => {
                  if (response.data.errors) {
                    commit("setErrorMessage", CONSTANTS.ERROR_MESSAGE);
                  }
                });
            }
          }
        })
        .catch(e => {
          console.error(e);
        });
    },

    fetchUserCriteria({ commit }) {
      return api
        .getUserCriteria()
        .then(response => {
          let userCriteria = response.data.userCriteria;
          commit("setUserCriteria", userCriteria);
        })
        .catch(e => { /* suppress error */ })
    },

  }
});

export default store;



// WEBPACK FOOTER //
// ./src/store/index.js